import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { ContentBlock } from "../PublicPage/ContentBlock";
import {
	BusyIndicator,
	Button,
	Form,
	FormItem,
	FormattedText,
	TextInput,
	ValidationError,
	renderReactDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	RequestEmergencyAdminAccessModel,
	requestEmergencyAdminAccess
} from "client/Api/AdminPage/adminPage";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isEmailAddress, isNotEmpty } from "client/Validation/rules";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { HttpRequestError } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { createRoot } from "react-dom/client";
import { PasswordResetSuccessfullyDialog } from "./PasswordResetSuccessfullyDialog";
import { ResetPasswordTimeoutExpiredDialog } from "./ResetPasswordTimeoutExpiredDialog";
import { ensureNotNil } from "client/Misc/utils";

export type RequestEmergencyAdminAccessProps = {
	resetUserPasswordRequestTimeout: number;
};

export const RequestEmergencyAdminAccess = (props: RequestEmergencyAdminAccessProps): ReactElement => {
	const {
		resetUserPasswordRequestTimeout
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<RequestEmergencyAdminAccessModel>({
		userName: ""
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<RequestEmergencyAdminAccessModel> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	const onSubmit = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await requestEmergencyAdminAccess(
					model,
					globalCancellation
				);

				alertManager.showSuccess(
					<FormattedText
						text={localize("_EmergencyAdminAccessRequestSucessfullDescription")}
						thingsToReplace={[
							{
								what: "{{Timeout}}",
								filler: resetUserPasswordRequestTimeout
							}
						]}
					/>
				);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let errorMessage: string | undefined;

				if (e instanceof HttpRequestError) {
					switch (e.statusCode) {
						case 400:
							errorMessage = localize("_NotAvalidEmailAddressFormat");
							break;
						case 403:
							errorMessage = localize("_NotAuthorized");
							break;
						default:
							// Nothing to do
							break;
					}
				}

				if (!errorMessage) {
					errorMessage = localize("_GeneralErrorMessage");
				}

				alertManager.showError(errorMessage);
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			model,
			resetUserPasswordRequestTimeout,
			formHandler.validation
		]
	);

	return (
		<ContentBlock size="large">
			{isBusy && (
				<BusyIndicator />
			)}

			<h1>{localize("_RequestTemporaryPasswordToAccessEmergencyAdminAccess")}</h1>
			<Form
				trackingId="admin-page-request-emergency-access"
				onSubmit={onSubmit}
			>
				<FormItem>
					<TextInput
						{...formHandler.for("userName").attachToTextInput()}
						placeholder={localize("_Username")}
					/>
					<ValidationError {...formHandler.for("userName").attachToValidationError()} />
				</FormItem>
				<Button
					trackingId="request-password"
					type="submit"
					kind="primary"
					text={localize("_RequestTemporaryPassword")}
				/>
			</Form>
		</ContentBlock>
	);
};
RequestEmergencyAdminAccess.displayName = "RequestEmergencyAdminAccess";

const validationConfiguration: IValidationConfiguration<RequestEmergencyAdminAccessModel> = createValidationConfiguration<RequestEmergencyAdminAccessModel>();

validationConfiguration.forScalar("userName")
	.add(
		isNotEmpty(localize("_ValidationRequired")),
		isEmailAddress(localize("_ValidationEmail"))
	);

export function passwordResetSuccessfully(): void {
	renderReactDialog(() => <PasswordResetSuccessfullyDialog />);
}

export function resetPasswordTimeoutExpired(): void {
	renderReactDialog(() => <ResetPasswordTimeoutExpiredDialog />);
}

export function init(resetUserPasswordRequestTimeout: number): void {
	createRoot(
		ensureNotNil(
			document.getElementById("admin-page-request-emergency-access")
		)
	)
		.render(
			<RequestEmergencyAdminAccess
				resetUserPasswordRequestTimeout={resetUserPasswordRequestTimeout}
			/>
		);
}
